import React, { useState, useEffect, useRef } from 'react';
import { contactsAPI, salesLeadAPI } from 'services/apisService';

const ContactHeader = ({ 
  contact, 
  statusDetails, 
  handleClose,
  isLead = false,
  leadStatus = null,
  leadContactId,
  onLeadUpdated,
  onEngagementUpdated,
  onContactUpdate
}) => {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [tempPhone, setTempPhone] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [currentEngagementStatus, setCurrentEngagementStatus] = useState(contact?.engagement_stage || 'cold_lead');
  const [showEngagementDropdown, setShowEngagementDropdown] = useState(false);
  const [updatingEngagement, setUpdatingEngagement] = useState(false);
  const [currentUserType, setCurrentUserType] = useState(contact?.userType || '');
  const [showUserTypeDropdown, setShowUserTypeDropdown] = useState(false);
  const [updatingUserType, setUpdatingUserType] = useState(false);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const nameRef = useRef(null);
  const engagementRef = useRef(null);
  const userTypeRef = useRef(null);
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const engagementOptions = [
    { value: 'cold_lead', label: 'Cold Lead', color: 'bg-gradient-to-r from-purple-100 to-purple-300 text-purple-800' },
    { value: 'warm_lead', label: 'Warm Lead', color: 'bg-gradient-to-r from-yellow-100 to-yellow-300 text-yellow-800' },
    { value: 'hot_lead', label: 'Hot Lead', color: 'bg-gradient-to-r from-red-500 to-orange-400 text-white' } 
  ];

  const userTypeOptions = [
    { value: 'Vendor', label: 'VENDOR' },
    { value: 'Enterprise', label: 'ENTERPRISE' },
    { value: 'FlexpertEnquiry', label: 'FLEXPERT' }
  ];

  useEffect(() => {
    if (contact?.engagement_stage) {
      setCurrentEngagementStatus(contact.engagement_stage);
    }
    if (contact?.userType) {
      setCurrentUserType(contact.userType);
    }
  }, [contact?.engagement_stage, contact?.userType]);

  const handleNameEdit = () => {
  setEditingName(true);
  setTempName(contact.name || '');
};

const handleNameSave = () => {
  handleUpdateField('name', tempName);
};

const handleNameCancel = () => {
  setEditingName(false);
  setTempName('');
};

  useEffect(() => {
  const handleClickOutside = (event) => {
    if (emailRef.current && !emailRef.current.contains(event.target)) {
      if (editingEmail) {
        handleEmailCancel();
      }
    }
    if (phoneRef.current && !phoneRef.current.contains(event.target)) {
      if (editingPhone) {
        handlePhoneCancel();
      }
    }
    if (nameRef.current && !nameRef.current.contains(event.target)) {
      if (editingName) {
        handleNameCancel();
      }
    }
    if (engagementRef.current && !engagementRef.current.contains(event.target)) {
      setShowEngagementDropdown(false);
    }
    if (userTypeRef.current && !userTypeRef.current.contains(event.target)) {
      setShowUserTypeDropdown(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [editingEmail, editingPhone, editingName]);


  const handleCopyEmail = () => {
    navigator.clipboard.writeText(contact.email);
    setCopiedEmail(true);
    setTimeout(() => {
      setCopiedEmail(false);
    }, 1000); 
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(contact.phone);
    setCopiedPhone(true);
    setTimeout(() => {
      setCopiedPhone(false);
    }, 1000); 
  };

  const handleUpdateField = async (field, value) => {
    if (value === contact[field]) {
      if (field === 'email') {
        setEditingEmail(false);
        setTempEmail('');
      } else if (field === 'phone') {
        setEditingPhone(false);
        setTempPhone('');
      } else if (field === 'name') {
        setEditingName(false);
        setTempName('');
      }
      return;
    }

    setIsUpdating(true);
    setUpdateError('');
    
    try {
      const updateData = { [field]: value };
      const updatedContact = await contactsAPI.updateContact(contact.id, updateData);
      
      Object.assign(contact, updatedContact);

      if (onContactUpdate) {
      onContactUpdate(updatedContact);
    }
      
      if (isLead && onLeadUpdated) {
        onLeadUpdated(leadContactId, updatedContact);
      }
      
      if (field === 'email') {
        setEditingEmail(false);
        setTempEmail('');
      } else if (field === 'phone') {
        setEditingPhone(false);
        setTempPhone('');
      } else if (field === 'name') {
        setEditingName(false);
        setTempName('');
      } else if (field === 'userType') {
        setCurrentUserType(value);
        setShowUserTypeDropdown(false);
      }
      
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setUpdateError(error.message || `Failed to update ${field}. Please try again.`);
      
      setTimeout(() => {
        setUpdateError('');
      }, 3000);
    } finally {
      setIsUpdating(false);
    }
  };

   const handleEngagementStatusChange = async (newStatus) => {
    if (newStatus === currentEngagementStatus) {
      setShowEngagementDropdown(false);
      return;
    }

    setUpdatingEngagement(true);
    setUpdateError('');
    
    try {
      const updateData = { engagement_stage: newStatus };
      await salesLeadAPI.updateSalesLeadEngagement(leadContactId, updateData);
      
      setCurrentEngagementStatus(newStatus);
      contact.engagement_stage = newStatus;
      setShowEngagementDropdown(false);
      
      if (isLead && onEngagementUpdated) {
        onEngagementUpdated(leadContactId, newStatus);
      }
      
    } catch (error) {
      console.error('Error updating engagement status:', error);
      setUpdateError(error.message || 'Failed to update engagement status. Please try again.');
      
      setTimeout(() => {
        setUpdateError('');
      }, 3000);
    } finally {
      setUpdatingEngagement(false);
    }
  };

  const handleUserTypeChange = async (newUserType) => {
    if (newUserType === currentUserType) {
      setShowUserTypeDropdown(false);
      return;
    }

    setUpdatingUserType(true);
    setUpdateError('');
    
    try {
      await handleUpdateField('userType', newUserType);
    } catch (error) {
      console.error('Error updating user type:', error);
      setUpdateError(error.message || 'Failed to update user type. Please try again.');
      
      setTimeout(() => {
        setUpdateError('');
      }, 3000);
    } finally {
      setUpdatingUserType(false);
    }
  };

  const handleEmailEdit = () => {
    setEditingEmail(true);
    setTempEmail(contact.email || '');
  };

  const handlePhoneEdit = () => {
    setEditingPhone(true);
    setTempPhone(contact.phone || '');
  };

  const handleEmailSave = () => {
    handleUpdateField('email', tempEmail);
  };

  const handlePhoneSave = () => {
    handleUpdateField('phone', tempPhone);
  };

  const handleEmailCancel = () => {
    setEditingEmail(false);
    setTempEmail('');
  };

  const handlePhoneCancel = () => {
    setEditingPhone(false);
    setTempPhone('');
  };

  const formatStatusLabel = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getDisplayStatus = () => {
    if (isLead && leadStatus) {
      return {
        label: formatStatusLabel(leadStatus),
        color: getLeadStatusColor(leadStatus)
      };
    }
    return statusDetails;
  };

  const getLeadStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'new_lead':
        return 'bg-blue-100 text-blue-800';
      case 'engaged':
        return 'bg-green-100 text-green-800';
      case 'demo_scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'demo_completed':
        return 'bg-indigo-100 text-indigo-800';
      case 'negotiation':
        return 'bg-yellow-100 text-yellow-800';
      case 'proposal_sent':
        return 'bg-orange-100 text-orange-800';
      case 'closed_won':
        return 'bg-orange-100 text-orange-800';
      case 'closed_lost':
        return 'bg-red-100 text-red-800';
      case 're_engagement':
        return 'bg-cyan-100 text-cyan-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCurrentEngagementOption = () => {
    return engagementOptions.find(option => option.value === currentEngagementStatus) || engagementOptions[0];
  };

  const getCurrentUserTypeOption = () => {
    const found = userTypeOptions.find(option => option.value === currentUserType);
    if (found) return found;
    // If userType is not set or doesn't match, default to first option but show current value if exists
    if (currentUserType) {
      let displayLabel = currentUserType;
      if (currentUserType === 'FlexpertEnquiry') {
        displayLabel = 'Flexpert';
      } else {
        displayLabel = currentUserType.toUpperCase();
      }
      return { value: currentUserType, label: displayLabel };
    }
    return userTypeOptions[0];
  };

  const currentStatusDetails = getDisplayStatus();
  const currentEngagementOption = getCurrentEngagementOption();
  const currentUserTypeOption = getCurrentUserTypeOption();

  return (
    <div className="bg-gradient-to-r from-[#3985b6] to-[#2d6f9a] px-6 py-4 flex flex-col rounded-tl-3xl sticky top-0 z-10">
      {updateError && (
        <div className="mb-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {updateError}
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-grow">
          <div className="flex gap-10">
            <div className="flex flex-col gap-1 w-80 min-w-0">
              <div className="flex items-center min-w-0" ref={nameRef}>
                {editingName ? (
                  <div className="flex items-center flex-grow">
                    <input
                      type="text"
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      className="bg-white text-gray-800 px-2 py-1 rounded text-lg  flex-grow"
                      disabled={isUpdating}
                    />
                    <div className="ml-2 flex gap-1">
                      <button 
                        className="bg-green-600 hover:bg-green-700 p-1 rounded text-white transition-colors" 
                        onClick={handleNameSave}
                        disabled={isUpdating}
                        title="Save name"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button 
                        className="bg-red-600 hover:bg-red-700 p-1 rounded text-white transition-colors" 
                        onClick={handleNameCancel}
                        disabled={isUpdating}
                        title="Cancel"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center flex-grow min-w-0">
                    <h2 className="text-2xl font-semibold text-white truncate min-w-0 max-w-full" title={contact.name}>
                      {contact.name}
                    </h2>
                    <button 
                      className="bg-white/15 hover:bg-white/25 p-1 rounded text-white transition-colors ml-2 flex-shrink-0" 
                      onClick={handleNameEdit} 
                      title="Edit name"
                      disabled={isUpdating}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <div>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${currentStatusDetails.color}`}>
                  {currentStatusDetails.label}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 w-96">
              {/* Email Row */}
              <div className="flex items-center" ref={emailRef}>
                <div className="w-16 bg-white/10 px-2 py-0.5 rounded mr-2">
                  <span className="text-white/90 text-xs font-bold uppercase tracking-wider">Email:</span>
                </div>
                <div className="flex-grow bg-white/5 px-2 py-0.5 rounded">
                  {editingEmail ? (
                    <input
                      type="email"
                      value={tempEmail}
                      onChange={(e) => setTempEmail(e.target.value)}
                      className="w-full bg-white text-gray-800 px-2 py-1 rounded text-sm"
                      disabled={isUpdating}
                    />
                  ) : (
                    <span  className="text-white hover:text-white/90 truncate text-sm">
                      {contact.email || '--'}
                    </span>
                  )}
                </div>
                
                {editingEmail ? (
                  <div className="ml-2 flex gap-1">
                    <button 
                      className="bg-green-600 hover:bg-green-700 p-1 rounded text-white transition-colors" 
                      onClick={handleEmailSave}
                      disabled={isUpdating}
                      title="Save email"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button 
                      className="bg-red-600 hover:bg-red-700 p-1 rounded text-white transition-colors" 
                      onClick={handleEmailCancel}
                      disabled={isUpdating}
                      title="Cancel"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="ml-2 flex gap-1">
                    <button 
                      className="bg-white/15 hover:bg-white/25 p-1 rounded text-white transition-colors" 
                      onClick={handleEmailEdit} 
                      title="Edit email"
                      disabled={isUpdating}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button 
                      className="bg-white/15 hover:bg-white/25 p-1 rounded text-white transition-colors" 
                      onClick={handleCopyEmail} 
                      title="Copy email"
                      disabled={isUpdating}
                    >
                      {copiedEmail ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Phone Row */}
              <div className="flex items-center" ref={phoneRef}>
                <div className="w-16 bg-white/10 px-2 py-0.5 rounded mr-2">
                  <span className="text-white/90 text-xs font-bold uppercase tracking-wider">Phone:</span>
                </div>
                <div className="flex-grow bg-white/5 px-2 py-0.5 rounded">
                  {editingPhone ? (
                    <input
                      type="tel"
                      value={tempPhone}
                      onChange={(e) => setTempPhone(e.target.value)}
                      className="w-full bg-white text-gray-800 px-2 py-1 rounded text-sm"
                      disabled={isUpdating}
                    />
                  ) : (
                    <span className="text-white hover:text-white/90 text-sm">
                      {contact.phone || '--'}
                    </span>
                  )}
                </div>
                
                {editingPhone ? (
                  <div className="ml-2 flex gap-1">
                    <button 
                      className="bg-green-600 hover:bg-green-700 p-1 rounded text-white transition-colors" 
                      onClick={handlePhoneSave}
                      disabled={isUpdating}
                      title="Save phone"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button 
                      className="bg-red-600 hover:bg-red-700 p-1 rounded text-white transition-colors" 
                      onClick={handlePhoneCancel}
                      disabled={isUpdating}
                      title="Cancel"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="ml-2 flex gap-1">
                    <button 
                      className="bg-white/15 hover:bg-white/25 p-1 rounded text-white transition-colors" 
                      onClick={handlePhoneEdit} 
                      title="Edit phone"
                      disabled={isUpdating}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button 
                      className="bg-white/15 hover:bg-white/25 p-1 rounded text-white transition-colors" 
                      onClick={handleCopyPhone} 
                      title="Copy phone"
                      disabled={isUpdating}
                    >
                      {copiedPhone ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1 w-44">
              <div className="text-white/90 text-xs font-bold uppercase tracking-wider mb-1">
                User Type
              </div>
              <div className="relative" ref={userTypeRef}>
                <button
                  onClick={() => setShowUserTypeDropdown(!showUserTypeDropdown)}
                  disabled={updatingUserType || isUpdating}
                  className="w-full rounded-lg px-4 py-2 text-left flex items-center justify-between shadow-sm hover:shadow-md transition-shadow disabled:opacity-50 text-sm font-medium bg-white/10 text-white border border-white/20 hover:bg-white/20"
                >
                  <span>
                    {currentUserTypeOption.label}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform ${showUserTypeDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showUserTypeDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-full overflow-hidden">
                    {userTypeOptions.map((option, index) => (
                      <button
                        key={option.value}
                        onClick={() => handleUserTypeChange(option.value)}
                        disabled={updatingUserType || isUpdating}
                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors disabled:opacity-50 text-gray-700 hover:bg-gray-50 ${
                          index === 0 ? 'rounded-t-lg' : ''
                        } ${
                          index === userTypeOptions.length - 1 ? 'rounded-b-lg' : ''
                        } ${
                          option.value === currentUserType ? 'bg-blue-50 font-semibold' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isLead && (
              <div className="flex flex-col gap-1 w-44">
                <div className="text-white/90 text-xs font-bold uppercase tracking-wider mb-1">
                  Engagement Stage
                </div>
                <div className="relative" ref={engagementRef}>
                  <button
                    onClick={() => setShowEngagementDropdown(!showEngagementDropdown)}
                    disabled={updatingEngagement}
                    className={`w-full rounded-lg px-4 py-2 text-left flex items-center justify-between shadow-sm hover:shadow-md transition-shadow disabled:opacity-50 text-sm font-medium ${currentEngagementOption.color}`}
                  >
                    <span>
                      {currentEngagementOption.label}
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showEngagementDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showEngagementDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-full overflow-hidden">
                      {engagementOptions.map((option, index) => (
                        <button
                          key={option.value}
                          onClick={() => handleEngagementStatusChange(option.value)}
                          disabled={updatingEngagement}
                          className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors disabled:opacity-50 text-gray-700 hover:bg-gray-50 ${
                            index === 0 ? 'rounded-t-lg' : ''
                          } ${
                            index === engagementOptions.length - 1 ? 'rounded-b-lg' : ''
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* <button 
          onClick={handleClose}
          className="rounded-full p-2 hover:bg-white/10 transition-colors text-white ml-4"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button> */}
      </div>
    </div>
  );
};

export default ContactHeader;